import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AUDIT_ACTION_LABELS,
  type AuditEventResponse,
  formatMoney,
  isOpenJob,
  JOB_KIND_LABELS,
  JOB_STATE_LABELS,
  type JobKind,
  type JobOffer,
  type JobResponse,
  type JobState,
  type JobTotals,
  jobStateProblem,
  nextJobStates,
  type PendingOffer,
} from '@k-management/shared';
import { type ReactNode, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate, formatTimestamp } from '../../lib/format.js';
import { MarginBadge } from '../quotes/CostsView.js';
import { quoteKeys } from '../quotes/quotes-ui.js';
import { ActiveCycle } from './ActiveCycle.js';
import { BudgetView } from './BudgetView.js';
import { JobDialog } from './JobDialog.js';
import { JobPeopleDialog } from './JobPeopleDialog.js';
import { Indicators } from './Indicators.js';
import { JobIndicatorsView } from './JobIndicatorsView.js';
import {
  FIGURE_ORDER,
  figureLabel,
  jobKeys,
  jobPath,
  JobStatePill,
  Meter,
  namesOf,
  overBudget,
} from './jobs-ui.js';
import { LinkOfferDialog } from './LinkOfferDialog.js';
import { PassiveCycle } from './PassiveCycle.js';
import { PeopleHours } from './PeopleHours.js';

type Tab = 'riepilogo' | 'indicatori' | 'attivo' | 'passivo' | 'persone' | 'budget' | 'storico';

/** The command that moves a commessa to a state. */
const STATE_COMMANDS: Record<JobState, string> = {
  in_preparazione: '',
  in_corso: 'Avvia',
  sospesa: 'Sospendi',
  chiusa: 'Chiudi',
  annullata: 'Annulla',
};

/**
 * The card of a commessa or a progetto (16/10/2026): data and figures, the
 * accepted offers, the history. The figures see it; who has the change of the
 * module changes it. A progetto (2.41) has its area, objective and indicators,
 * its costs and budget, and is changed also by its area or by its PM.
 */
export function JobPage({ kind }: { kind: JobKind }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canRegistry = useCan('anagrafica.read');
  const [tab, setTab] = useState<Tab>('riepilogo');
  const [editing, setEditing] = useState(false);
  const [figures, setFigures] = useState(false);
  const [moving, setMoving] = useState<JobState | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addingChild, setAddingChild] = useState(false);
  const job = useQuery({
    queryKey: jobKeys.job(id),
    queryFn: () => apiRequest<JobResponse>(`/jobs/${id}`),
  });
  const data = job.data;
  usePageTitle(data ? data.code : JOB_KIND_LABELS[kind]);
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: jobKeys.all }),
      queryClient.invalidateQueries({ queryKey: quoteKeys.all }),
    ]);
  const move = useMutation({
    mutationFn: (state: JobState) =>
      apiRequest<JobResponse>(`/jobs/${id}/state`, { method: 'POST', body: { state } }),
    onSuccess: async () => {
      setMoving(null);
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: () => apiRequest<void>(`/jobs/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: jobKeys.all });
      void navigate(kind === 'commessa' ? '/commesse' : '/progetti');
    },
  });

  const edit = data?.you.edit ?? false;
  const admin = data?.you.admin ?? false;
  const commands: (Command | false)[] = data
    ? [
        edit && {
          key: 'edit',
          label: 'Modifica',
          icon: 'edit',
          primary: true,
          onClick: () => setEditing(true),
        },
        edit && { key: 'people', label: 'Figure', icon: 'users', onClick: () => setFigures(true) },
        // A madre takes no offers: they go to its sottocommesse (17/10/2026).
        edit &&
          kind === 'commessa' &&
          data.childCount === 0 &&
          isOpenJob(data.state) && {
            key: 'offer',
            label: 'Collega un’offerta',
            icon: 'plus',
            onClick: () => setAdding(true),
          },
        edit &&
          !data.parentId &&
          isOpenJob(data.state) && {
            key: 'child',
            label: kind === 'commessa' ? 'Nuova sottocommessa' : 'Nuovo sottoprogetto',
            icon: 'plus',
            onClick: () => setAddingChild(true),
          },
        ...nextJobStates(data.state)
          .filter((state) => edit && (state !== 'annullata' || admin))
          .map((state): Command => ({
            key: `state-${state}`,
            label:
              data.state === 'chiusa' && state === 'in_corso'
                ? 'Riapri'
                : data.state === 'sospesa' && state === 'in_corso'
                  ? 'Riprendi'
                  : STATE_COMMANDS[state],
            icon: state === 'annullata' ? 'close' : 'check',
            onClick: () => setMoving(state),
          })),
        admin &&
          data.state === 'in_preparazione' &&
          data.offerCount === 0 &&
          data.childCount === 0 && {
            key: 'delete',
            label: 'Elimina',
            icon: 'close',
            onClick: () => setDeleting(true),
          },
      ]
    : [];
  useCommands(commands.filter((command): command is Command => Boolean(command)));

  if (job.isError) {
    return (
      <section className="stack">
        <p className="error">{job.error.message}</p>
        <Link to={kind === 'commessa' ? '/commesse' : '/progetti'}>
          Torna a{kind === 'commessa' ? 'lle commesse' : 'i progetti'}
        </Link>
      </section>
    );
  }
  if (!data) return <p className="muted">Caricamento…</p>;

  const tabs: [Tab, string][] = [
    ['riepilogo', 'Riepilogo'],
    ['indicatori', 'Indicatori'],
    ...(kind === 'commessa'
      ? ([
          ['attivo', 'Ciclo attivo'],
          ['passivo', 'Ciclo passivo'],
          ['persone', 'Persone e ore'],
          ['budget', 'Budget'],
        ] as [Tab, string][])
      : ([
          ['passivo', 'Costi esterni'],
          ['persone', 'Persone e ore'],
          ['budget', 'Budget'],
        ] as [Tab, string][])),
    ['storico', 'Storico'],
  ];
  const problem = moving
    ? jobStateProblem(
        {
          kind: data.kind,
          state: data.state,
          startOn: data.startOn,
          endOn: data.endOn,
          people: data.people,
          offerCount: data.offerCount,
          childStates: data.children.map((child) => child.state),
        },
        moving,
      )
    : null;

  return (
    <section className="stack">
      <div className="page-header">
        <div className="stack tight">
          <p className="muted small">
            <Link to={kind === 'commessa' ? '/commesse' : '/progetti'}>
              {kind === 'commessa' ? 'Commesse' : 'Progetti'}
            </Link>{' '}
            ›{' '}
            {data.parent && (
              <>
                {data.parent.visible ? (
                  <Link to={jobPath({ id: data.parent.id, kind })}>{data.parent.code}</Link>
                ) : (
                  data.parent.code
                )}{' '}
                ›{' '}
              </>
            )}
            {data.code}
          </p>
          <h1>
            {data.code} · {data.name}
          </h1>
          <div className="flags">
            <JobStatePill state={data.state} />
            {kind === 'progetto' && (
              <span>{data.areaName ? `Area ${data.areaName}` : 'Senza area'}</span>
            )}
            {kind === 'progetto' && overBudget(data.costs) && (
              <span className="pill bad">Oltre il budget</span>
            )}
            {data.clientName &&
              (canRegistry && data.clientId ? (
                <Link to={`/anagrafica/${data.clientId}`}>{data.clientName}</Link>
              ) : (
                <span>{data.clientName}</span>
              ))}
          </div>
        </div>
        {kind === 'commessa' && (
          <div className="quote-total">
            <small className="muted">Offerte accettate</small>
            <strong>{formatMoney(data.offersNet)}</strong>
            <small className="muted">
              imponibile
              {data.childCount > 0 &&
                ` · somma di ${data.childCount === 1 ? '1 sottocommessa' : `${data.childCount} sottocommesse`}`}
            </small>
          </div>
        )}
        {kind === 'progetto' && data.costs && (
          <div className="quote-total">
            <small className="muted">Consuntivo su budget dei costi</small>
            <strong>
              {formatMoney(data.costs.actual)} / {formatMoney(data.costs.budget)}
            </strong>
            {Number(data.costs.budget) > 0 && (
              <Meter
                share={Number(data.costs.actual) / Number(data.costs.budget)}
                over={overBudget(data.costs)}
              />
            )}
          </div>
        )}
      </div>
      {data.state === 'in_preparazione' && edit && (
        <p className="notice info">
          {kind === 'commessa'
            ? 'In preparazione: per avviarla servono PM, Account e le date previste.'
            : 'In preparazione: per avviarlo servono il PM e le date previste.'}
        </p>
      )}
      <nav
        className="subtabs"
        aria-label={kind === 'commessa' ? 'Sezioni della commessa' : 'Sezioni del progetto'}
      >
        {tabs.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={tab === key ? 'subtab selected' : 'subtab'}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </nav>
      {tab === 'riepilogo' && (
        <>
          <Summary job={data} />
          {kind === 'progetto' && <Indicators job={data} />}
          {data.childCount > 0 && <Children job={data} />}
          {kind === 'commessa' && <Offers job={data} onChanged={refresh} />}
        </>
      )}
      {tab === 'indicatori' && <JobIndicatorsView job={data} />}
      {tab === 'attivo' && <ActiveCycle job={data} />}
      {tab === 'passivo' && <PassiveCycle job={data} />}
      {tab === 'persone' && <PeopleHours job={data} />}
      {tab === 'budget' && <BudgetView job={data} />}
      {tab === 'storico' && <History id={data.id} />}
      {editing && <JobDialog job={data} kind={kind} onClose={() => setEditing(false)} />}
      {figures && <JobPeopleDialog job={data} onClose={() => setFigures(false)} />}
      {adding && <AddOfferDialog job={data} onClose={() => setAdding(false)} />}
      {addingChild && (
        <JobDialog
          job={null}
          kind={kind}
          parent={data}
          onClose={() => setAddingChild(false)}
          onSaved={(saved) => void navigate(jobPath(saved))}
        />
      )}
      {moving && (
        <ConfirmDialog
          title={`${data.code}: ${JOB_STATE_LABELS[moving].toLowerCase()}`}
          confirmLabel={problem ? 'Ho capito' : STATE_COMMANDS[moving] || 'Conferma'}
          busy={move.isPending}
          error={problem ?? (move.isError ? move.error.message : null)}
          onConfirm={() => (problem ? setMoving(null) : move.mutate(moving))}
          onCancel={() => setMoving(null)}
        >
          <p>
            Da «{JOB_STATE_LABELS[data.state]}» a «{JOB_STATE_LABELS[moving]}».
            {moving === 'chiusa' &&
              (kind === 'commessa'
                ? ' Una commessa chiusa non prende nuove offerte; resta consultabile e si può riaprire.'
                : ' Un progetto chiuso resta consultabile e si può riaprire: indica l’esito dei suoi indicatori.')}
            {moving === 'annullata' && ' Non si torna indietro.'}
          </p>
        </ConfirmDialog>
      )}
      {deleting && (
        <ConfirmDialog
          title={`Eliminare ${data.code}?`}
          confirmLabel="Elimina"
          busy={remove.isPending}
          error={remove.isError ? remove.error.message : null}
          onConfirm={() => remove.mutate()}
          onCancel={() => setDeleting(false)}
        >
          <p>Il codice non si riusa. L’eliminazione resta nel registro attività.</p>
        </ConfirmDialog>
      )}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children || '—'}</dd>
    </div>
  );
}

function Summary({ job }: { job: JobResponse }) {
  return (
    <div className="employee-cards job-cards">
      <div className="card">
        <h3>Dati</h3>
        <dl className="facts">
          {job.kind === 'commessa' && (
            <Row label="Cliente">{job.clientName && <strong>{job.clientName}</strong>}</Row>
          )}
          {job.kind === 'commessa' && (
            <Row label="Si fattura a">{job.billingClient?.name ?? 'il cliente'}</Row>
          )}
          {job.kind === 'progetto' && <Row label="Area">{job.areaName}</Row>}
          {job.parent && (
            <Row label={job.kind === 'commessa' ? 'Commessa madre' : 'Progetto'}>
              {job.parent.visible ? (
                <Link to={jobPath({ id: job.parent.id, kind: job.kind })}>
                  {job.parent.code} · {job.parent.name}
                </Link>
              ) : (
                `${job.parent.code} · ${job.parent.name}`
              )}
            </Row>
          )}
          <Row label="Descrizione">{job.description}</Row>
          <Row label="Inizio previsto">{job.startOn && formatDate(job.startOn)}</Row>
          <Row label="Fine prevista">{job.endOn && formatDate(job.endOn)}</Row>
          <Row label="Note">{job.notes}</Row>
          <Row label={job.kind === 'commessa' ? 'Creata' : 'Creato'}>
            {formatTimestamp(job.createdAt)}
            {job.createdBy && ` da ${job.createdBy}`}
          </Row>
        </dl>
      </div>
      <div className="card">
        <h3>Figure</h3>
        <dl className="facts">
          {FIGURE_ORDER.filter((figure) => job.kind === 'commessa' || figure === 'pm').map(
            (figure) => {
              const people = job.people.filter((person) => person.figure === figure);
              return (
                <Row key={figure} label={figureLabel(figure)}>
                  {people.length > 0 &&
                    people.map((person) => (
                      <div key={person.userId}>
                        {person.name}
                        {figure === 'commerciale' && (
                          <small className="muted">
                            {' '}
                            · {person.canSee ? 'vede la commessa' : 'non vede la commessa'}
                          </small>
                        )}
                      </div>
                    ))}
                </Row>
              );
            },
          )}
        </dl>
        <p className="muted small">
          {job.kind === 'commessa'
            ? 'PM e Account vedono tutta la commessa; il commerciale solo se abilitato. Le modifiche le fa chi ha il permesso del modulo.'
            : 'Il PM vede e aggiorna il progetto. Lo vedono e lo modificano anche gli utenti dell’area, secondo il loro accesso.'}
        </p>
      </div>
    </div>
  );
}

/** The sottocommesse of a madre (17/10/2026): those the user sees, with their sum. */
function Children({ job }: { job: JobResponse }) {
  if (job.kind === 'progetto') return <ProjectChildren job={job} />;
  return (
    <section className="card stack">
      <h3>
        Sottocommesse <span className="count-pill">{job.childCount}</span>
      </h3>
      <div className="table-scroll">
        <table className="lines">
          <thead>
            <tr>
              <th>Sottocommessa</th>
              <th>PM</th>
              <th>Account</th>
              <th className="right">Offerte</th>
              <th className="right">Ciclo attivo</th>
              <th className="right">Ciclo passivo</th>
              <th className="right">Margine</th>
              <th>Stato</th>
            </tr>
          </thead>
          <tbody>
            {job.children.map((child) => (
              <tr key={child.id}>
                <td>
                  <Link to={jobPath(child)}>
                    <strong>
                      {child.code} · {child.name}
                    </strong>
                  </Link>
                </td>
                <td>{namesOf(child.people, 'pm')}</td>
                <td>{namesOf(child.people, 'account')}</td>
                <td className="right">{child.offerCount}</td>
                <TotalsCells totals={child.totals} />
                <td>
                  <JobStatePill state={child.state} />
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={4} className="muted">
                Totale della commessa
              </td>
              {job.totals && <TotalsCells totals={job.totals} strong />}
              <td />
            </tr>
          </tbody>
        </table>
      </div>
      <p className="muted small">
        La commessa è la somma delle sue sottocommesse: offerte, e poi rate e costi, stanno nelle
        sottocommesse. Si chiude quando tutte sono chiuse o annullate.
      </p>
    </section>
  );
}

/** The sottoprogetti of a progetto (2.41): their costs at budget and actual, and the sum. */
function ProjectChildren({ job }: { job: JobResponse }) {
  return (
    <section className="card stack">
      <h3>
        Sottoprogetti <span className="count-pill">{job.childCount}</span>
      </h3>
      <div className="table-scroll">
        <table className="lines">
          <thead>
            <tr>
              <th>Sottoprogetto</th>
              <th>PM</th>
              <th>Inizio – fine</th>
              <th className="right">Budget costi</th>
              <th className="right">Consuntivo</th>
              <th>Stato</th>
            </tr>
          </thead>
          <tbody>
            {job.children.map((child) => (
              <tr key={child.id}>
                <td>
                  <Link to={jobPath(child)}>
                    <strong>
                      {child.code} · {child.name}
                    </strong>
                  </Link>
                </td>
                <td>{namesOf(child.people, 'pm')}</td>
                <td>
                  {child.startOn ? formatDate(child.startOn) : '…'} –{' '}
                  {child.endOn ? formatDate(child.endOn) : '…'}
                </td>
                <td className="right">{formatMoney(child.totals.passive)}</td>
                <td className="right">{formatMoney(child.totals.received)}</td>
                <td>
                  <JobStatePill state={child.state} />
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={3} className="muted">
                Totale del progetto
              </td>
              <td className="right">
                <strong>{job.totals ? formatMoney(job.totals.passive) : '—'}</strong>
              </td>
              <td className="right">
                <strong>{job.totals ? formatMoney(job.totals.received) : '—'}</strong>
              </td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
      <p className="muted small">
        Il progetto è la somma dei suoi sottoprogetti: i costi stanno nei sottoprogetti. Si chiude
        quando tutti sono chiusi o annullati.
      </p>
    </section>
  );
}

/** Ciclo attivo, passivo and margin of a sottocommessa or of the whole madre (2.27). */
function TotalsCells({ totals, strong = false }: { totals: JobTotals; strong?: boolean }) {
  const money = (value: string) =>
    strong ? <strong>{formatMoney(value)}</strong> : formatMoney(value);
  return (
    <>
      <td className="right">
        {money(totals.active)}
        <small className="muted block">fatturato {formatMoney(totals.invoiced)}</small>
      </td>
      <td className="right">
        {money(totals.passive)}
        <small className="muted block">ricevuto {formatMoney(totals.received)}</small>
      </td>
      <td className="right">
        {money(totals.margin.margin)}
        <small className="block">
          <MarginBadge margin={totals.margin} />
        </small>
        {totals.expenses.light && (
          <small className="block">
            <span className={`pill ${totals.expenses.light === 'verde' ? 'ok' : 'bad'}`}>
              spese {formatMoney(totals.expenses.passive)} su {formatMoney(totals.expenses.active)}
            </span>
          </small>
        )}
      </td>
    </>
  );
}

function Offers({ job, onChanged }: { job: JobResponse; onChanged: () => Promise<unknown> }) {
  const [removing, setRemoving] = useState<JobOffer | null>(null);
  const unlink = useMutation({
    mutationFn: (quoteId: string) =>
      apiRequest<JobResponse>(`/jobs/${job.id}/offers/${quoteId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setRemoving(null);
      await onChanged();
    },
  });
  // A madre shows the offers of its sottocommesse, each with its own (17/10/2026).
  const withChild = job.childCount > 0;
  if (job.offers.length === 0) {
    return (
      <section className="card job-offers-empty">
        <h3>
          Offerte <span className="count-pill">{job.offerCount}</span>
        </h3>
        <span className="muted">Nessuna offerta collegata.</span>
      </section>
    );
  }
  return (
    <section className="card stack">
      <h3>
        Offerte <span className="count-pill">{job.offerCount}</span>
      </h3>
      <div className="table-scroll">
        <table className="lines">
          <thead>
            <tr>
              <th>Offerta</th>
              {withChild && <th>Sottocommessa</th>}
              <th>Oggetto</th>
              <th>Commerciale</th>
              <th>Data</th>
              <th>Collegata</th>
              <th className="right">Imponibile</th>
              {job.you.admin && <th />}
            </tr>
          </thead>
          <tbody>
            {job.offers.map((offer) => (
              <tr key={offer.quoteId}>
                <td>
                  <Link to={`/offerte/${offer.quoteId}`}>{offer.label}</Link>
                </td>
                {withChild && (
                  <td>
                    <Link to={`/commesse/${offer.jobId}`}>{offer.jobCode}</Link>
                  </td>
                )}
                <td>{offer.referenceName ?? '—'}</td>
                <td>{offer.owner ?? '—'}</td>
                <td>{formatDate(offer.date)}</td>
                <td className="muted small">
                  {formatTimestamp(offer.linkedAt)}
                  {offer.linkedBy && ` da ${offer.linkedBy}`}
                </td>
                <td className="right">{formatMoney(offer.net)}</td>
                {job.you.admin && (
                  <td className="right">
                    <button type="button" className="link" onClick={() => setRemoving(offer)}>
                      Togli…
                    </button>
                  </td>
                )}
              </tr>
            ))}
            <tr>
              <td colSpan={withChild ? 6 : 5} className="muted">
                Totale offerte accettate
              </td>
              <td className="right">
                <strong>{formatMoney(job.offersNet)}</strong>
              </td>
              {job.you.admin && <td />}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="muted small">
        Le offerte restano documenti a sé: un clic le apre. Un’offerta accettata per errore si
        toglie e torna «inviata»; il resto della commessa non cambia.
      </p>
      {removing && (
        <ConfirmDialog
          title={`Togliere ${removing.label}?`}
          confirmLabel="Togli dalla commessa"
          busy={unlink.isPending}
          error={unlink.isError ? unlink.error.message : null}
          onConfirm={() => unlink.mutate(removing.quoteId)}
          onCancel={() => setRemoving(null)}
        >
          <p>
            L’offerta esce da {job.code} e torna «inviata», come prima dell’accettazione. Senza
            offerte la commessa si può annullare.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}

/** An accepted offer of the same client, still without its commessa, into this one. */
function AddOfferDialog({ job, onClose }: { job: JobResponse; onClose: () => void }) {
  const [chosen, setChosen] = useState<PendingOffer | null>(null);
  const pending = useQuery({
    queryKey: jobKeys.pending,
    queryFn: () => apiRequest<PendingOffer[]>('/jobs/pending-offers'),
  });
  const offers = (pending.data ?? []).filter((offer) => offer.clientId === job.clientId);
  if (chosen) {
    return (
      <LinkOfferDialog
        offer={{ ...chosen, openJobs: chosen.openJobs.filter((open) => open.id === job.id) }}
        onClose={onClose}
        onLinked={() => undefined}
      />
    );
  }
  return (
    <ConfirmDialog
      title={`Collega a ${job.code}`}
      confirmLabel="Chiudi"
      onConfirm={onClose}
      onCancel={onClose}
    >
      {pending.isPending && <p className="muted">Caricamento…</p>}
      {pending.data && offers.length === 0 && (
        <p className="muted">
          Nessuna offerta accettata di {job.clientName} in attesa di commessa.
        </p>
      )}
      <ul className="plain-list">
        {offers.map((offer) => (
          <li key={offer.quoteId} className="row between">
            <span>
              {offer.label} · {formatDate(offer.date)} · {formatMoney(offer.net)}
            </span>
            <button type="button" className="link" onClick={() => setChosen(offer)}>
              Scegli
            </button>
          </li>
        ))}
      </ul>
    </ConfirmDialog>
  );
}

function History({ id }: { id: string }) {
  const history = useQuery({
    queryKey: jobKeys.history(id),
    queryFn: () => apiRequest<AuditEventResponse[]>(`/jobs/${id}/history`),
  });
  return (
    <div className="card stack">
      {history.data?.length === 0 && <p className="muted">Nessuna modifica.</p>}
      {history.data && history.data.length > 0 && (
        <ul className="plain-list">
          {history.data.map((event) => (
            <li key={event.id}>
              <span className="muted small">
                {formatTimestamp(event.occurredAt)} · {event.actorEmail ?? 'sistema'} ·{' '}
                {AUDIT_ACTION_LABELS[event.action]}
              </span>
              <div>{event.summary}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
